import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiMail, FiUser, FiShield, FiActivity } from 'react-icons/fi';

export default function InviteUserModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'Bookkeeper'
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call for sending an email invitation via Supabase Auth
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Since we don't have server-side admin privileges enabled in the frontend, 
      // we'll pass the form data back to simulate the success in the UI.
      onSuccess(formData);
      onClose();
      setFormData({ email: '', firstName: '', lastName: '', role: 'Bookkeeper' });
    } catch (err) {
      setError("Failed to send invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-100">
          <h2 className="text-xl font-black text-slate-900">Invite Team Member</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-rose-50 text-rose-600 rounded-xl text-sm font-bold flex items-center gap-2">
              <SafeIcon icon={FiActivity} /> {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">Email Address</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="email" 
                required
                placeholder="colleague@company.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">First Name</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text" 
                  required
                  placeholder="Jane"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">Last Name</label>
              <div className="relative">
                <input 
                  type="text" 
                  required
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium transition-all"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">Role / Permissions</label>
            <div className="relative">
              <SafeIcon icon={FiShield} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select 
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium appearance-none transition-all"
              >
                <option value="Bookkeeper">Bookkeeper (Standard Access)</option>
                <option value="Accounting Admin">Accounting Admin</option>
                <option value="Super Admin">Super Admin (Full Access)</option>
              </select>
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              {formData.role === 'Super Admin' && "Has full access to all company settings and billing."}
              {formData.role === 'Accounting Admin' && "Can manage finances, run payroll, and invite standard members."}
              {formData.role === 'Bookkeeper' && "Can view dashboard and perform standard tasks based on module permissions."}
            </p>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-2">
            <button 
              type="button" 
              onClick={onClose}
              className="px-5 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="px-5 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-w-[120px]"
            >
              {loading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              Send Invite
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}